import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";

type FETPlansResponse =
  paths["/patients/{patientId}/frozen-embryo-transfer-plans"]["get"]["responses"]["200"]["content"]["application/json"];

export type FETPlan = FETPlansResponse["data"][number];
export type FETPlanVersion = FETPlan["versions"][number];

export function fetPlanForCycleQueryKey(
  patientId: string | null,
  cycleId: string | null,
) {
  return ["fet-plan-for-cycle", patientId, cycleId] as const;
}

export function selectFETPlanForCycle(
  plans: FETPlan[],
  cycleId: string,
): FETPlan | null {
  return (
    plans.find(
      (plan) =>
        plan.snapshots.some((snapshot) => snapshot.cycleId === cycleId) ||
        plan.versions.some((version) => version.sourceCycleId === cycleId),
    ) ?? null
  );
}

export function useFETPlanForCycle(options: {
  cycleId: string | null;
  enabled?: boolean;
}) {
  const { client, patientId } = useApiClient();
  const enabled = options.enabled ?? true;

  return useQuery<FETPlan | null>({
    queryKey: fetPlanForCycleQueryKey(patientId, options.cycleId),
    queryFn: async () => {
      if (!patientId || !options.cycleId) return null;

      const response = await client.GET(
        "/patients/{patientId}/frozen-embryo-transfer-plans",
        { params: { path: { patientId } } },
      );
      if (response.error) {
        throwApiError(
          response.error,
          "Failed to load the accepted FET plan",
          "fet-plan-for-cycle",
        );
      }
      return selectFETPlanForCycle(response.data?.data ?? [], options.cycleId);
    },
    enabled: enabled && !!patientId && !!options.cycleId,
  });
}
